import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, FileText, LayoutTemplate, Plus, Search, Sparkles, Upload } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type {
  AiDraftProposalDto,
  AiImportProposalDto,
  DocumentSummaryDto,
  DocumentTypeDto,
  Paginated,
  TemplateDto,
} from '@imsenta/shared';
import { PageHeader } from '@/components/AppShell';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Modal,
  Select,
  Spinner,
  StatusBadge,
  Textarea,
  useToast,
} from '@/components/ui';
import { api, ApiError, downloadPost } from '@/lib/api';
import { daysUntil, fmtDate, fmtRelative } from '@/lib/format';
import { useSession } from '@/lib/session';

export function DocumentsPage() {
  const navigate = useNavigate();
  const { can } = useSession();
  const { push } = useToast();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get('q') ?? '');
  const [createOpen, setCreateOpen] = useState(params.get('new') === '1');

  const status = params.get('status') ?? '';
  const typeKey = params.get('typeKey') ?? '';
  const sort = params.get('sort') ?? 'updated';
  const order = params.get('order') ?? 'desc';
  const page = Number(params.get('page') ?? 1);

  // Debounce the search box so typing does not fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      const next = new URLSearchParams(params);
      if (search) next.set('q', search);
      else next.delete('q');
      next.delete('page');
      if (next.toString() !== params.toString()) setParams(next, { replace: true });
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['documents', { q: params.get('q'), status, typeKey, sort, order, page }],
    queryFn: () =>
      api.get<Paginated<DocumentSummaryDto>>('/documents', {
        q: params.get('q') ?? undefined,
        status: status || undefined,
        typeKey: typeKey || undefined,
        sort,
        order,
        page,
        pageSize: 25,
      }),
  });

  const { data: types } = useQuery({
    queryKey: ['document-types'],
    queryFn: () => api.get<{ items: DocumentTypeDto[] }>('/document-types'),
  });

  const update = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete('page');
    setParams(next);
  };

  return (
    <>
      <PageHeader
        title="Documents"
        description="Every policy, standard and procedure in your library."
        actions={
          <>
            {can('document:export') ? (
              <Button
                icon={<Download className="size-4" />}
                onClick={async () => {
                  try {
                    await downloadPost('/documents/export/bundle', { format: 'markdown' }, 'policy-library.zip');
                    push('Library exported as Markdown', 'success');
                  } catch (error) {
                    push(error instanceof ApiError ? error.message : 'Export failed', 'danger');
                  }
                }}
              >
                Export library
              </Button>
            ) : null}
            {can('document:create') ? (
              <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setCreateOpen(true)}>
                New document
              </Button>
            ) : null}
          </>
        }
      />

      <Card className="mb-4 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-56 flex-1">
            <Search className="absolute top-2.5 left-3 size-4 text-ink-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search titles, references and content…"
              className="pl-9"
            />
          </div>
          <Select value={typeKey} onChange={(e) => update('typeKey', e.target.value)} className="w-auto min-w-40">
            <option value="">All types</option>
            {types?.items.map((type) => (
              <option key={type.key} value={type.key}>
                {type.name}
              </option>
            ))}
          </Select>
          <Select value={status} onChange={(e) => update('status', e.target.value)} className="w-auto min-w-36">
            <option value="">Active</option>
            <option value="draft">Draft</option>
            <option value="in_review">In review</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
            <option value="all">All, including archived</option>
          </Select>
          <Select
            value={`${sort}:${order}`}
            onChange={(e) => {
              const [nextSort, nextOrder] = e.target.value.split(':');
              const next = new URLSearchParams(params);
              next.set('sort', nextSort!);
              next.set('order', nextOrder!);
              setParams(next);
            }}
            className="w-auto min-w-44"
          >
            <option value="updated:desc">Recently updated</option>
            <option value="created:desc">Newest first</option>
            <option value="title:asc">Title A–Z</option>
            <option value="reference:asc">Reference</option>
            <option value="review:asc">Review date</option>
          </Select>
        </div>
      </Card>

      <Card>
        {isLoading ? (
          <Spinner label="Loading documents…" />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} title="Could not load your documents" />
        ) : !data?.items.length ? (
          <EmptyState
            icon={<FileText className="size-10" />}
            title={params.get('q') ? 'No documents match that search' : 'No documents yet'}
            description={params.get('q') ? 'Try a different term, or clear the filters.' : 'Start from a template, or import existing Markdown.'}
            action={can('document:create') ? <Button variant="primary" onClick={() => setCreateOpen(true)}>New document</Button> : undefined}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-[13.5px]">
                <thead className="border-b border-ink-200 bg-ink-50/60 text-[11.5px] tracking-wide text-ink-500 uppercase">
                  <tr>
                    <th className="px-4 py-2.5 font-semibold">Reference</th>
                    <th className="px-4 py-2.5 font-semibold">Title</th>
                    <th className="px-4 py-2.5 font-semibold">Type</th>
                    <th className="px-4 py-2.5 font-semibold">Status</th>
                    <th className="px-4 py-2.5 font-semibold">Version</th>
                    <th className="px-4 py-2.5 font-semibold">Owner</th>
                    <th className="px-4 py-2.5 font-semibold">Review due</th>
                    <th className="px-4 py-2.5 font-semibold">Updated</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-100">
                  {data.items.map((doc) => {
                    const days = daysUntil(doc.reviewDue);
                    return (
                      <tr key={doc.id} className="cursor-pointer hover:bg-ink-50" onClick={() => navigate(`/documents/${doc.id}`)}>
                        <td className="px-4 py-2.5">
                          <span className="font-mono text-[12px] text-ink-500">{doc.reference}</span>
                        </td>
                        <td className="max-w-xs px-4 py-2.5">
                          <Link to={`/documents/${doc.id}`} className="font-medium text-ink-800 hover:text-brand-700" onClick={(e) => e.stopPropagation()}>
                            {doc.title}
                          </Link>
                          {doc.openChangeRequests > 0 ? (
                            <Badge tone="info" className="ml-2">
                              {doc.openChangeRequests} open
                            </Badge>
                          ) : null}
                        </td>
                        <td className="px-4 py-2.5 text-ink-600">{doc.documentTypeName}</td>
                        <td className="px-4 py-2.5">
                          <StatusBadge status={doc.status} />
                        </td>
                        <td className="px-4 py-2.5 text-ink-600">{doc.currentVersion?.label ?? <span className="text-ink-400">—</span>}</td>
                        <td className="max-w-32 truncate px-4 py-2.5 text-ink-600">{doc.owner?.name ?? '—'}</td>
                        <td className="px-4 py-2.5">
                          {doc.reviewDue ? (
                            <span className={days !== null && days < 0 ? 'font-medium text-rose-600' : days !== null && days < 30 ? 'text-amber-600' : 'text-ink-600'}>
                              {fmtDate(doc.reviewDue)}
                            </span>
                          ) : (
                            <span className="text-ink-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-ink-500">{fmtRelative(doc.updatedAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {data.totalPages > 1 ? (
              <div className="flex items-center justify-between border-t border-ink-200 px-4 py-3">
                <p className="text-[12.5px] text-ink-500">
                  Page {data.page} of {data.totalPages} · {data.total} documents
                </p>
                <div className="flex gap-2">
                  <Button size="sm" disabled={data.page <= 1} onClick={() => update('page', String(data.page - 1))}>
                    Previous
                  </Button>
                  <Button size="sm" disabled={data.page >= data.totalPages} onClick={() => update('page', String(data.page + 1))}>
                    Next
                  </Button>
                </div>
              </div>
            ) : null}
          </>
        )}
      </Card>

      <CreateDocumentModal
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          const next = new URLSearchParams(params);
          next.delete('new');
          setParams(next, { replace: true });
        }}
      />
    </>
  );
}

type Mode = 'template' | 'markdown' | 'ai' | 'draft';

/** The four ways to start, in the order most people will want them. */
const METHODS: { id: Mode; label: string; description: string; icon: typeof FileText; ai?: boolean }[] = [
  {
    id: 'template',
    label: 'From a template',
    description: 'Your house structure, ready to write into. The usual way.',
    icon: LayoutTemplate,
  },
  {
    id: 'draft',
    label: 'Draft from a brief',
    description: 'Describe what it needs to cover and get a complete first draft to edit.',
    icon: Sparkles,
    ai: true,
  },
  {
    id: 'ai',
    label: 'Import a document',
    description: 'A Word file, PDF or pasted text you already have, restructured into sections.',
    icon: Upload,
    ai: true,
  },
  {
    id: 'markdown',
    label: 'Paste Markdown',
    description: 'Markdown with headings, imported as written. No model involved.',
    icon: FileText,
  },
];

export function CreateDocumentModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { push } = useToast();
  /*
   * Two steps rather than four tabs over one form. Choosing how to start is a
   * real decision — the four ways have little in common beyond a title — and
   * putting every method's fields on one screen made all of them harder to read.
   */
  const [step, setStep] = useState<'method' | 'details'>('method');
  const [mode, setMode] = useState<Mode>('template');
  const [form, setForm] = useState({ title: '', documentTypeKey: '', templateId: '', summary: '', importMarkdown: '' });
  const [error, setError] = useState<string | null>(null);
  /** The smart-import source, before it has been sent anywhere. */
  const [aiSource, setAiSource] = useState<{ text: string; fileBase64: string; filename: string } | null>(null);
  const [proposal, setProposal] = useState<AiImportProposalDto | AiDraftProposalDto | null>(null);
  const [brief, setBrief] = useState('');
  const proposalRef = useRef<HTMLDivElement>(null);

  /*
   * The form above a proposal is long, so the draft lands below the fold —
   * which is the one thing the person was waiting to read. Bring it to them.
   *
   * Optional-called because this is a convenience, not behaviour: jsdom has no
   * scrollIntoView, and a nicety must never be able to break the dialog.
   */
  useEffect(() => {
    if (proposal) proposalRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [proposal]);

  const { data: aiStatus } = useQuery({
    queryKey: ['ai-status'],
    queryFn: () => api.get<{ enabled: boolean; reason: string | null }>('/ai/status'),
    enabled: open,
    retry: false,
  });

  const { data: types } = useQuery({
    queryKey: ['document-types'],
    queryFn: () => api.get<{ items: DocumentTypeDto[] }>('/document-types'),
    enabled: open,
  });

  const { data: templates } = useQuery({
    queryKey: ['templates', form.documentTypeKey],
    queryFn: () => api.get<{ items: TemplateDto[] }>('/templates', { documentTypeKey: form.documentTypeKey || undefined }),
    enabled: open && Boolean(form.documentTypeKey),
  });

  useEffect(() => {
    if (!form.documentTypeKey && types?.items.length) {
      setForm((current) => ({ ...current, documentTypeKey: types.items[0]!.key }));
    }
  }, [types, form.documentTypeKey]);

  useEffect(() => {
    const preferred = templates?.items.find((t) => t.isDefault) ?? templates?.items[0];
    setForm((current) => ({ ...current, templateId: preferred?.id ?? '' }));
  }, [templates]);

  /*
   * The proposal is only Markdown, so accepting it goes through the ordinary
   * create-with-Markdown path below. There is no separate "create from AI"
   * endpoint to keep in step, and imported content meets the same validation
   * as content somebody pasted in.
   */
  const analyse = useMutation({
    mutationFn: () =>
      api.post<AiImportProposalDto>('/ai/import', {
        text: aiSource?.text || undefined,
        fileBase64: aiSource?.fileBase64 || undefined,
        filename: aiSource?.filename || undefined,
        documentTypeKey: form.documentTypeKey || undefined,
      }),
    onSuccess: (result) => {
      setProposal(result);
      setForm((current) => ({
        ...current,
        title: current.title.trim() || result.title || '',
        summary: current.summary || result.summary || '',
        importMarkdown: result.markdown,
      }));
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not read that document'),
  });

  const draft = useMutation({
    mutationFn: () =>
      api.post<AiDraftProposalDto>('/ai/draft', {
        brief: brief.trim(),
        documentTypeKey: form.documentTypeKey || undefined,
        // A house template exists for a reason: when one is chosen it is the
        // structure, and the brief fills it.
        templateId: form.templateId || null,
        title: form.title.trim() || undefined,
      }),
    onSuccess: (result) => {
      setProposal(result);
      setForm((current) => ({
        ...current,
        title: current.title.trim() || result.title || '',
        summary: current.summary || result.summary || '',
        importMarkdown: result.markdown,
      }));
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not draft that document'),
  });

  const readFile = async (file: File) => {
    setError(null);
    setProposal(null);
    const buffer = await file.arrayBuffer();
    let binary = '';
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]!);
    setAiSource({ text: '', fileBase64: btoa(binary), filename: file.name });
  };

  const create = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>('/documents', {
        title: form.title.trim(),
        documentTypeKey: form.documentTypeKey,
        templateId: mode === 'template' ? form.templateId || null : null,
        summary: form.summary || undefined,
        // Both the paste and the smart-import modes arrive here as Markdown;
        // only the template mode leaves it unset.
        importMarkdown: mode === 'template' ? undefined : form.importMarkdown,
      }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      push('Document created', 'success');
      onClose();
      navigate(`/documents/${created.id}/edit`);
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not create the document'),
  });

  const selectedTemplate = templates?.items.find((t) => t.id === form.templateId);

  // Reopening starts over: a half-finished proposal from last time is worse
  // than nothing, because it looks like it belongs to what you are doing now.
  useEffect(() => {
    if (!open) return;
    setStep('method');
    setError(null);
    setProposal(null);
    setAiSource(null);
    setBrief('');
    setForm((current) => ({ ...current, title: '', summary: '', importMarkdown: '' }));
  }, [open]);

  const chosen = METHODS.find((method) => method.id === mode)!;
  const aiMethod = mode === 'ai' || mode === 'draft';
  const readyToCreate =
    Boolean(form.title.trim()) && Boolean(form.documentTypeKey) && (mode === 'template' || Boolean(form.importMarkdown.trim()));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={step === 'method' ? 'New document' : chosen.label}
      description={step === 'method' ? 'How would you like to start?' : chosen.description}
      width="lg"
      footer={
        step === 'method' ? (
          <Button onClick={onClose}>Cancel</Button>
        ) : (
          <>
            <Button onClick={() => setStep('method')}>Back</Button>
            <Button
              variant="primary"
              loading={create.isPending}
              disabled={!readyToCreate}
              onClick={() => {
                setError(null);
                create.mutate();
              }}
            >
              Create document
            </Button>
          </>
        )
      }
    >
      {step === 'method' ? (
        <div className="grid gap-2.5 sm:grid-cols-2">
          {METHODS.map((method) => (
            <button
              key={method.id}
              type="button"
              onClick={() => {
                setMode(method.id);
                setError(null);
                setStep('details');
              }}
              className="rounded-xl border border-ink-200 p-4 text-left transition-colors hover:border-brand-300 hover:bg-brand-50/40"
            >
              <span className="flex items-center gap-2">
                <method.icon className="size-4 text-brand-600" />
                <span className="text-[14px] font-semibold text-ink-900">{method.label}</span>
                {method.ai ? <Badge tone="brand">AI</Badge> : null}
              </span>
              <span className="mt-1.5 block text-[12.5px] leading-relaxed text-ink-600">{method.description}</span>
            </button>
          ))}
        </div>
      ) : (
      <div className="space-y-4">
        {error ? <Alert tone="danger">{error}</Alert> : null}

        {aiMethod && aiStatus && !aiStatus.enabled ? (
          <Alert tone="warning">{aiStatus.reason ?? 'AI assistance is switched off for this organisation.'}</Alert>
        ) : null}

        <Field label="Title" required>
          <Input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Information Security Policy"
            autoFocus
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Document type" required hint="Determines the reference prefix, e.g. POL-001.">
            <Select value={form.documentTypeKey} onChange={(e) => setForm({ ...form, documentTypeKey: e.target.value })}>
              {types?.items.map((type) => (
                <option key={type.key} value={type.key}>
                  {type.name} ({type.referencePrefix})
                </option>
              ))}
            </Select>
          </Field>

          {mode === 'template' || mode === 'draft' ? (
            <Field
              label="Template"
              hint={
                mode === 'draft'
                  ? 'Its sections become the structure the draft fills. Leave it blank to let the model propose one.'
                  : selectedTemplate?.description
              }
            >
              <Select value={form.templateId} onChange={(e) => setForm({ ...form, templateId: e.target.value })}>
                <option value="">
                  {mode === 'draft' ? 'No template — let the model structure it' : 'No template — start blank'}
                </option>
                {templates?.items.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                    {template.isDefault ? ' (default)' : ''}
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}
        </div>

        <Field label="Summary" hint="A one-line description shown in listings.">
          <Input value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} placeholder="Optional" />
        </Field>

        {mode === 'draft' ? (
          <div className="space-y-3">
            <Field
              label="What does this document need to cover?"
              required
              hint="Scope, who it applies to, the obligations you need, any framework you are working to. The more you give it, the less it has to leave blank."
            >
              <Textarea
                rows={6}
                value={brief}
                onChange={(e) => {
                  setProposal(null);
                  setBrief(e.target.value);
                }}
                placeholder="An access control standard for a 200-person software company. Cover joiners, movers and leavers, privileged access, and quarterly reviews. We use Okta and AWS."
              />
            </Field>

            <Button
              variant="primary"
              icon={<Sparkles className="size-4" />}
              loading={draft.isPending}
              disabled={brief.trim().length < 20}
              onClick={() => {
                setError(null);
                draft.mutate();
              }}
            >
              {proposal ? 'Draft it again' : 'Write the draft'}
            </Button>

            {draft.isPending ? (
              <p className="text-[12.5px] text-ink-500">Writing — a full document takes a little longer than a section.</p>
            ) : null}

            {proposal ? (
              <div ref={proposalRef}>
                <ProposalPreview proposal={proposal} />
              </div>
            ) : null}
          </div>
        ) : null}

        {mode === 'ai' ? (
          <div className="space-y-3">
            <Field
              label="The document you already have"
              hint="PDF, Word (.docx), Markdown or plain text. A scanned PDF has no text to read, so it will not work."
            >
              <input
                type="file"
                accept=".pdf,.docx,.md,.markdown,.txt"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) void readFile(file);
                }}
                className="block w-full text-[13px] text-ink-600 file:mr-3 file:rounded-md file:border-0 file:bg-ink-100 file:px-3 file:py-1.5 file:text-[13px] file:font-medium file:text-ink-700 hover:file:bg-ink-200"
              />
            </Field>

            <Field label="…or paste its text">
              <Textarea
                rows={6}
                value={aiSource?.text ?? ''}
                onChange={(e) => {
                  setProposal(null);
                  setAiSource({ text: e.target.value, fileBase64: '', filename: '' });
                }}
                placeholder="Paste the policy here."
                className="text-[12.5px]"
              />
            </Field>

            <Button
              variant="primary"
              icon={<Sparkles className="size-4" />}
              loading={analyse.isPending}
              disabled={!aiSource?.text.trim() && !aiSource?.fileBase64}
              onClick={() => {
                setError(null);
                analyse.mutate();
              }}
            >
              {proposal ? 'Read it again' : 'Read the document'}
            </Button>

            {analyse.isPending ? (
              <p className="text-[12.5px] text-ink-500">
                Reading {aiSource?.filename || 'the text'} — a long policy takes a few moments.
              </p>
            ) : null}

            {proposal ? (
              <div ref={proposalRef}>
                <ProposalPreview proposal={proposal} />
              </div>
            ) : null}
          </div>
        ) : null}

        {mode === 'markdown' ? (
          <Field
            label="Markdown"
            required
            hint="Headings become sections. Front matter is read into the document's metadata."
          >
            <Textarea
              rows={10}
              value={form.importMarkdown}
              onChange={(e) => setForm({ ...form, importMarkdown: e.target.value })}
              placeholder={'---\ntitle: My Policy\nowner: Head of Risk\n---\n\n# My Policy\n\n## Purpose\n\n…'}
              className="font-mono text-[12.5px]"
            />
          </Field>
        ) : mode === 'template' && selectedTemplate ? (
          <div className="rounded-lg border border-ink-200 bg-ink-50 px-3.5 py-3">
            <p className="mb-1.5 text-[11.5px] font-semibold tracking-wide text-ink-500 uppercase">
              <Upload className="mr-1 inline size-3" />
              Sections you will start with
            </p>
            <ol className="grid gap-x-4 gap-y-0.5 text-[12.5px] text-ink-600 sm:grid-cols-2">
              {selectedTemplate.content.sections.map((section, index) => (
                <li key={section.id}>
                  <span className="mr-1.5 text-ink-400">{index + 1}.</span>
                  {section.title}
                </li>
              ))}
            </ol>
          </div>
        ) : null}
      </div>
      )}
    </Modal>
  );
}

/**
 * What the model proposed, before anything exists.
 *
 * Shared by the two AI modes because the review is the same job either way:
 * see the structure, read the caveats, then decide. The warnings are never
 * collapsed or styled down — a tidy-looking outline is exactly when nobody
 * checks whether a clause went missing.
 */
function ProposalPreview({ proposal }: { proposal: AiImportProposalDto | AiDraftProposalDto }) {
  const source = 'source' in proposal ? proposal.source : null;
  const placeholders = 'placeholders' in proposal ? proposal.placeholders : [];

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-ink-200 bg-ink-50 px-3.5 py-3">
        <p className="mb-1.5 text-[12px] font-semibold text-ink-700">
          {proposal.outline.length} section{proposal.outline.length === 1 ? '' : 's'}
          {source?.filename ? ` found in ${source.filename}` : ''}
        </p>
        <ol className="max-h-40 space-y-0.5 overflow-y-auto text-[12.5px] text-ink-600">
          {proposal.outline.map((entry, index) => (
            <li key={`${entry.clause}-${index}`} style={{ paddingLeft: `${(entry.level - 1) * 14}px` }}>
              <span className="mr-1.5 text-ink-400">{entry.clause || '—'}</span>
              {entry.title}
            </li>
          ))}
        </ol>
      </div>

      {/*
        The document itself. An outline and a list of caveats are not enough to
        judge a proposal by — least of all a drafted one, where every word came
        from a model and nobody has read any of it yet. The HTML is our own
        renderer's output from content we parsed and validated, the same path
        the catalog uses, not anything the model handed us directly.
      */}
      <div className="overflow-hidden rounded-lg border border-ink-200">
        <p className="border-b border-ink-100 bg-ink-50 px-3.5 py-2 text-[12px] font-semibold text-ink-700">
          {proposal.title ? `Preview — ${proposal.title}` : 'Preview'}
        </p>
        <div className="max-h-96 overflow-y-auto px-4 py-3">
          <div className="pf-prose text-[13.5px]" dangerouslySetInnerHTML={{ __html: proposal.html }} />
        </div>
      </div>

      {placeholders.length ? (
        <div className="rounded-lg border border-ink-200 px-3.5 py-3">
          <p className="mb-1.5 text-[12px] font-semibold text-ink-700">Left for you to fill in</p>
          <div className="flex flex-wrap gap-1.5">
            {placeholders.map((placeholder) => (
              <span key={placeholder} className="rounded bg-amber-100 px-1.5 py-0.5 text-[12px] text-amber-900">
                [{placeholder}]
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {proposal.warnings.map((warning) => (
        <Alert key={warning} tone="warning">
          {warning}
        </Alert>
      ))}
    </div>
  );
}
